/**
 * Antrenman kaydı — saf mantık. Program üreticisi ne *önerdiğini* biliyor,
 * burası ne *yapıldığını* tutuyor.
 *
 * Neden bu ayrım önemli: ilerleme (bir sonraki aşamada gelecek yük ayarı)
 * öneriden değil, yapılandan hesaplanır. "3×12 önerildi" ile "3×8 yapıldı,
 * RPE 9" çok farklı iki durum.
 */

import type { Pattern, WorkoutDay } from './program'

/**
 * Set tipi.
 *
 * `warmup` sadece bir etiket değil — **ilerleme hesabından tamamen düşüyor.**
 * Isınma setini saymak motoru bozardı: 20 kg × 5 ısınma, "hedef tekrarın
 * altında kaldın" sinyali üretip yükü düşürtürdü. Aynı şekilde hacme de
 * girmiyor; ısınma iş değil hazırlık.
 *
 * `drop` (ağırlık düşürerek devam) ve `failure` (yetmezliğe kadar) ise
 * yapılan iştir, sayılıyor — sadece geçmişe bakarken "burada ne olmuş"
 * sorusunun cevabı olsun diye işaretleniyor.
 */
export type SetKind = 'normal' | 'warmup' | 'drop' | 'failure'

export interface SetLog {
  reps: number | null
  seconds: number | null
  weightKg: number | null
  done: boolean
  /** Boşsa normal — eski kayıtlar alan taşımıyor. */
  kind?: SetKind
}

export const SET_KIND_LABEL: Record<SetKind, string> = {
  normal: 'Normal set',
  warmup: 'Isınma — hacme ve ilerlemeye sayılmaz',
  drop: 'Drop set — ağırlık düşürüp devam',
  failure: 'Yetmezlik — bir tekrar daha yapamadın',
}

export const SET_KIND_SHORT: Record<SetKind, string> = {
  normal: '', warmup: 'I', drop: 'D', failure: 'F',
}

const KIND_ORDER: SetKind[] = ['normal', 'warmup', 'drop', 'failure']

/** Tek dokunuşla sıradaki tipe geç — spor salonunda menü açmak pahalı. */
export function nextKind(k: SetKind | undefined): SetKind {
  return KIND_ORDER[(KIND_ORDER.indexOf(k ?? 'normal') + 1) % KIND_ORDER.length]
}

/** İş seti mi — ısınma dışındaki her şey. Hacim ve ilerleme bunları sayıyor. */
export function isWorkSet(s: SetLog): boolean {
  return (s.kind ?? 'normal') !== 'warmup'
}

export interface ExerciseLog {
  slug: string
  /** Gösterilen ad — orijinal (İngilizce) hareket adı. */
  name: string
  pattern: Pattern | string
  unit: 'rep' | 'sec'
  targetSets: number
  targetLow: number
  targetHigh: number
  sets: SetLog[]
  /** Algılanan zorluk 1–10. null = henüz girilmedi. */
  rpe: number | null
  skipped: boolean
}

export interface Session {
  date: string            // YYYY-MM-DD, yerel gün
  /** Şablon harfi — bölünmeye göre A..E. */
  day: string
  note: string | null
  completedAt: string | null
  exercises: ExerciseLog[]
  updatedAt?: string
}

export interface LastLog {
  date: string
  log: ExerciseLog
}

/**
 * RPE = algılanan zorluk. "Kaç tekrar daha yapabilirdin" sorusunun cevabı.
 * Ağırlık kaydeden ama zorluk kaydetmeyen bir sistem ilerlemeyi göremez:
 * aynı 3×10, bir gün rahat bir gün ölümcül olabilir.
 */
export const RPE_SCALE: { value: number; short: string; detail: string }[] = [
  { value: 1,  short: 'Çok hafif', detail: 'Neredeyse hiç zorlanmadım' },
  { value: 2,  short: 'Hafif',     detail: 'Isınma gibiydi' },
  { value: 3,  short: 'Hafif',     detail: 'Rahat, sohbet edebilirdim' },
  { value: 4,  short: 'Rahat',     detail: 'Çok tekrar daha yapabilirdim' },
  { value: 5,  short: 'Orta',      detail: '5–6 tekrar daha yapabilirdim' },
  { value: 6,  short: 'Orta',      detail: '4 tekrar daha yapabilirdim' },
  { value: 7,  short: 'Zorlayıcı', detail: '3 tekrar daha yapabilirdim' },
  { value: 8,  short: 'Zor',       detail: '2 tekrar daha yapabilirdim' },
  { value: 9,  short: 'Çok zor',   detail: '1 tekrar daha yapabilirdim' },
  { value: 10, short: 'Sonuna kadar', detail: 'Bir tekrar daha yapamazdım' },
]

/** Hedeflenen bölge: 6–8 arası. Altı fazla kolay, üstü sürdürülemez. */
export function rpeVerdict(rpe: number | null): 'low' | 'ok' | 'high' | null {
  if (rpe === null) return null
  if (rpe <= 5) return 'low'
  if (rpe >= 9) return 'high'
  return 'ok'
}

export function emptySet(): SetLog {
  return { reps: null, seconds: null, weightKg: null, done: false, kind: 'normal' }
}

/** Bir setin tipini değiştirir. */
export function setSetKind(s: Session, exIndex: number, setIndex: number, kind: SetKind): Session {
  return {
    ...s,
    exercises: s.exercises.map((e, i) => (i !== exIndex ? e : {
      ...e,
      sets: e.sets.map((x, k) => (k === setIndex ? { ...x, kind } : x)),
    })),
  }
}

/**
 * Geçen seferki AYNI SIRADAKİ setin özeti — "önceki: 20 kg × 10".
 *
 * Hevy'nin en çok işe yarayan detayı bu: hedef aralığı değil, geçen sefer
 * gerçekte ne yaptığını satırın içinde görüyorsun. Hedefi hatırlamak için
 * geçmişe gitmek gerekmiyor, karşılaştırma gözünün önünde.
 */
export function prevSetText(prev: ExerciseLog | undefined, setIndex: number): string | null {
  const st = prev?.sets.filter((x) => x.done)[setIndex]
  if (!st) return null
  const unit = prev!.unit === 'sec' ? `${st.seconds ?? 0} sn` : `${st.reps ?? 0}`
  return st.weightKg != null && st.weightKg > 0
    ? `${st.weightKg} kg × ${prev!.unit === 'sec' ? unit : st.reps ?? 0}`
    : unit
}

/** Dışarıdan ağırlık eklenebilen ekipmanlar — kg alanı sadece bunlarda gösteriliyor. */
const LOADABLE = new Set(['dumbbell', 'kettlebell', 'barbell', 'machine', 'cable'])

/**
 * Bu harekette yük yazılabilir mi.
 *
 * Direnç bandı ŞARTA BAĞLI: yalnızca kullanıcı elindeki bantların kg
 * değerlerini girdiyse. Girmediyse kg kutusunu açmanın anlamı yok — kullanıcı
 * oraya ne yazacağını bilmez ve boş bir sütun eklemiş oluruz. Bant girildiği
 * anda hem kutu açılıyor hem ilerleme basamaklara oturuyor (bkz. bant.ts).
 */
export function isLoadable(equipment: string, bandsKg: readonly number[] = []): boolean {
  if (LOADABLE.has(equipment)) return true
  return equipment === 'bands' && bandsKg.some((x) => x > 0)
}

/**
 * Günün programından boş bir kayıt taslağı üretir.
 * Geçen seferki ağırlık başlangıç değeri olarak taşınır — her seferinde
 * "kaç kiloyla yapıyordum" diye düşünmek gereksiz sürtünme.
 */
/** Bir harekete uygulanacak ilerleme kararı — `training.ts` üretiyor. */
export interface Prescribed {
  nextWeightKg: number | null
  nextReps: number | null
}

export function draftFromDay(
  date: string,
  day: WorkoutDay,
  last: Record<string, LastLog> = {},
  prescribed: Record<string, Prescribed> = {},
): Session {
  return {
    date,
    day: day.label,
    note: null,
    completedAt: null,
    exercises: day.exercises.map((p) => {
      const prev = last[p.exercise.slug]?.log
      const carry = prev ? topWeight(prev) : null

      // İlerleme kararı varsa yeni yük ve tekrar hedefi BURADA uygulanıyor.
      // Sadece "20 → 21 kg" yazıp kutuya 20 koymak, programın kendini yeniden
      // yapılandırdığı iddiasını boşa çıkarıyordu — kullanıcı yine elle yazıyordu.
      const rx = prescribed[p.exercise.slug]

      return {
        slug: p.exercise.slug,
        name: p.exercise.nameEn,
        pattern: p.pattern,
        unit: p.unit,
        targetSets: p.sets,
        targetLow: rx?.nextReps ?? p.low,
        targetHigh: p.high,
        sets: Array.from({ length: p.sets }, () => ({
          ...emptySet(), weightKg: rx?.nextWeightKg ?? carry,
        })),
        rpe: null,
        skipped: false,
      }
    }),
  }
}

/** Bir harekette kullanılan en yüksek ağırlık. */
/** Isınma setleri ilerlemeye ve hacme sayılmaz — bkz. SetKind. */
export function topWeight(e: ExerciseLog): number | null {
  const ws = e.sets.filter((s) => s.done && isWorkSet(s) && s.weightKg != null)
    .map((s) => s.weightKg as number)
  return ws.length ? Math.max(...ws) : null
}

/** Toplam iş: tekrar × ağırlık. Ağırlıksız hareketlerde sadece tekrar sayısı. */
export function volume(e: ExerciseLog): number {
  return e.sets
    .filter((s) => s.done && isWorkSet(s))
    .reduce((sum, s) => sum + (s.reps ?? 0) * (s.weightKg ?? 1), 0)
}

export function exerciseDone(e: ExerciseLog): boolean {
  return e.skipped || (e.sets.length > 0 && e.sets.every((s) => s.done))
}

export function progress(s: Session): { done: number; total: number; pct: number } {
  const active = s.exercises.filter((e) => !e.skipped)
  const total = active.reduce((n, e) => n + e.sets.length, 0)
  const done = active.reduce((n, e) => n + e.sets.filter((x) => x.done).length, 0)
  return { done, total, pct: total === 0 ? 0 : Math.round((done / total) * 100) }
}

/** En az bir set yapılmadan antrenman "bitti" sayılmaz — yanlışlıkla işaretlemeyi engelliyor. */
export function canFinish(s: Session): boolean {
  return progress(s).done > 0
}

/** RPE'si girilmemiş, yapılmış hareketler. Bitirmeden önce uyarı için. */
export function missingRpe(s: Session): string[] {
  return s.exercises
    .filter((e) => !e.skipped && e.sets.some((x) => x.done) && e.rpe === null)
    .map((e) => e.name)
}

// ── Değişmez güncelleme yardımcıları ────────────────────────────────────────
// React state'i doğrudan mutasyona uğratmamak için; hepsi yeni nesne döner.

export function updateSet(
  s: Session, exIndex: number, setIndex: number, patch: Partial<SetLog>,
): Session {
  return mapExercise(s, exIndex, (e) => ({
    ...e,
    sets: e.sets.map((x, i) => (i === setIndex ? { ...x, ...patch } : x)),
  }))
}

/**
 * Bir seti işaretlerken tekrar/süre boşsa hedefin alt sınırıyla dolduruluyor.
 * Sebebi: en sık kullanım "programı olduğu gibi yaptım" — her kutuyu elle
 * doldurtmak o durumu cezalandırır.
 */
export function toggleSet(s: Session, exIndex: number, setIndex: number): Session {
  const e = s.exercises[exIndex]
  const cur = e.sets[setIndex]
  if (cur.done) return updateSet(s, exIndex, setIndex, { done: false })

  const filled: Partial<SetLog> = { done: true }
  if (e.unit === 'rep' && cur.reps == null) filled.reps = e.targetLow
  if (e.unit === 'sec' && cur.seconds == null) filled.seconds = e.targetLow
  return updateSet(s, exIndex, setIndex, filled)
}

export function setRpe(s: Session, exIndex: number, rpe: number | null): Session {
  return mapExercise(s, exIndex, (e) => ({ ...e, rpe }))
}

export function toggleSkip(s: Session, exIndex: number): Session {
  return mapExercise(s, exIndex, (e) => ({ ...e, skipped: !e.skipped }))
}

export function addSet(s: Session, exIndex: number): Session {
  return mapExercise(s, exIndex, (e) =>
    e.sets.length >= 12 ? e : { ...e, sets: [...e.sets, { ...emptySet(), weightKg: topWeight(e) }] })
}

export function removeSet(s: Session, exIndex: number): Session {
  return mapExercise(s, exIndex, (e) =>
    e.sets.length <= 1 ? e : { ...e, sets: e.sets.slice(0, -1) })
}

/** Düğmeyi devre dışı bırakmak için — son set silinemiyor. */
export function removeSetGuard(e: ExerciseLog): boolean {
  return e.sets.length > 1
}

function mapExercise(s: Session, i: number, fn: (e: ExerciseLog) => ExerciseLog): Session {
  return { ...s, exercises: s.exercises.map((e, k) => (k === i ? fn(e) : e)) }
}

// ── Görüntüleme ─────────────────────────────────────────────────────────────

/** "3 × 8–12 tekrar" — kayıttaki hedef. program.ts'teki reçete metninin kayıt karşılığı. */
export function targetText(e: ExerciseLog): string {
  return `${e.targetSets} × ${e.targetLow}–${e.targetHigh} ${e.unit === 'rep' ? 'tekrar' : 'sn'}`
}

/** "3×10 · 12 kg · RPE 7" — geçen seferki kaydın tek satır özeti. */
export function logSummary(e: ExerciseLog): string {
  const done = e.sets.filter((s) => s.done)
  if (done.length === 0) return 'kayıt yok'

  const vals = done.map((s) => (e.unit === 'sec' ? s.seconds : s.reps) ?? 0)
  const same = vals.every((v) => v === vals[0])
  const unit = e.unit === 'sec' ? ' sn' : ''
  const repText = same ? `${done.length}×${vals[0]}${unit}` : `${done.length} set: ${vals.join('/')}${unit}`

  const w = topWeight(e)
  return [repText, w ? `${w} kg` : null, e.rpe ? `RPE ${e.rpe}` : null]
    .filter(Boolean).join(' · ')
}

/** "12 Tem" — geçen sefer ne zamandı. */
export function shortDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  const AY = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara']
  if (!y || !m || !d) return iso
  return `${d} ${AY[m - 1]}`
}

/** Sunucuya giden gövde — updatedAt ve completedAt sunucuda yönetiliyor. */
export function toUpsert(s: Session): {
  day: string; note: string | null; completed: boolean; exercises: ExerciseLog[]
} {
  return {
    day: s.day,
    note: s.note,
    completed: s.completedAt !== null,
    exercises: s.exercises,
  }
}
